/**
 * `/admin-panel`, `/admin-panel/<section>` and `/admin-panel/<section>/<sub>`.
 *
 * The same shape `news-route.ts` uses, and for the same stated reason: this is
 * a pure path parser plus a `navigate()` that pushes history, not a router. The
 * argument in `deep-link.ts` still holds — a history stack around a WebGL canvas
 * is not warranted — and the admin panel is the second place where a real,
 * refreshable URL genuinely earns its keep. An operator sends a colleague a link
 * to a player's file; a hard refresh on `/admin-panel/players/<id>` has to land there.
 *
 * Both the Vite dev server and the production static server already fall back to
 * `index.html` for unknown paths, so nothing on that side had to change. The path
 * is the template's: its Caddyfile redirects the old `/admin` and `/admin/*`
 * (once a Kottster container) to `/admin-panel` with a 308, and the SPA serves
 * it from there.
 */

/** The sections, in the order the panel's rail lists them. */
export const ADMIN_SECTIONS = [
  'overview',
  'players',
  'withdrawals',
  'kyc',
  'payments',
  'payment-config',
  'invoices',
  'content',
  'seasons',
  'settings',
  'payment-methods',
  'meta',
  'offers',
  'market',
  'shop',
  'cosmetics',
  'config',
  'keys',
  'consents',
  'roles',
  'audit',
  'email',
] as const;

export type AdminSection = (typeof ADMIN_SECTIONS)[number];

export const ADMIN_SECTION_LABELS: Readonly<Record<AdminSection, string>> = {
  overview: 'Overview',
  players: 'Players',
  withdrawals: 'Withdrawals',
  kyc: 'KYC queue',
  payments: 'Payments',
  'payment-config': 'Payment config',
  invoices: 'Invoices',
  content: 'Content',
  seasons: 'Seasons',
  settings: 'Settings',
  'payment-methods': 'Payment methods',
  meta: 'Meta tags',
  offers: 'Offers',
  market: 'Market',
  shop: 'Shop',
  cosmetics: 'Cosmetics',
  config: 'Game config',
  keys: 'API keys',
  consents: 'Cookie consent',
  roles: 'Roles',
  audit: 'Audit log',
  email: 'E-mail',
};

/**
 * Where the panel is: a section, plus an optional second path segment that the
 * section itself interprets — a player id under `players`, a tab under
 * `payments`. One generic segment rather than a per-section route table: the
 * parser stays a dozen lines and each section owns what its segment means.
 */
export interface AdminRoute {
  section: AdminSection;
  sub: string | null;
}

/**
 * `null` when the path is not the admin panel at all.
 *
 * An unknown section falls back to `overview` rather than to `null`, the same
 * "absent is not broken" call `parseNewsRoute` makes for a slug-less article
 * link: somebody who mistypes a section should land in the panel, not be thrown
 * back to the game with no explanation.
 */
export function parseAdminRoute(pathname: string): AdminRoute | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/admin-panel') return { section: 'overview', sub: null };

  const match = /^\/admin-panel\/([^/]+)(?:\/([^/]+))?$/.exec(path);
  if (!match) return null;

  const section = decodeURIComponent(match[1]).trim().toLowerCase();
  if (!(ADMIN_SECTIONS as readonly string[]).includes(section)) {
    return { section: 'overview', sub: null };
  }
  return { section: section as AdminSection, sub: match[2] ? decodeURIComponent(match[2]) : null };
}

/** Reads the live URL. The one impure line, kept out of the parser. */
export function readAdminRoute(): AdminRoute | null {
  if (typeof window === 'undefined') return null;
  return parseAdminRoute(window.location.pathname);
}

/** The URL of a section, and of its second segment when it has one. */
export function adminPath(section: AdminSection, sub: string | null = null): string {
  if (section === 'overview') return '/admin-panel';
  const base = `/admin-panel/${section}`;
  return sub ? `${base}/${encodeURIComponent(sub)}` : base;
}

/**
 * Moves to a section.
 *
 * Dispatches `popstate` itself so a programmatic navigation is indistinguishable
 * from the browser's own back button — exactly what `navigateNews` does, and
 * what lets `App.vue` hold one listener rather than one per caller.
 */
export function navigateAdmin(section: AdminSection, sub: string | null = null): void {
  const path = adminPath(section, sub);
  if (window.location.pathname !== path) {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

/** Back to the game, address bar and all. */
export function leaveAdmin(): void {
  window.history.pushState({}, '', '/');
  window.dispatchEvent(new PopStateEvent('popstate'));
}
